<template>
  <div class="card">
    <div class="grid md:grid-flow-col grid-cols-1 md:grid-cols-2">
      <!-- title -->
      <div class="mb-8 md:mb-16">
        <span class="title"> {{ $t(title) }} </span>
      </div>

      <!-- color scale -->
      <div v-if="haveGeoInsights" class="w-full mb-6 pt-2 md:pr-2">
        <div class="relative w-full mb-2">
          <span class="absolute bottom-0 left-0 gradiant-label">High</span>
          <span class="absolute bottom-0 right-0 gradiant-label">Low</span>
        </div>
        <div v-if="isFilterSelected" class="gradiant w-full" v-bind:style="gradiantStyle"></div>
      </div>
    </div>

    <!-- Map -->
    <div class="relative w-full mb-4 overflow-hidden md:mb-16">

      <div class="zoom-controller absolute">
        <span class="flex w-full zoom-in-icon speakol speakol-plus justify-center items-center" v-on:click="zoomIn">
        </span>
        <span class="flex w-full zoom-out-icon justify-center items-center" v-on:click="zoomOut"> - </span>
      </div>

      <!-- <pan-zoom @init="onInitPanZoom" :options="panZoomOptions"> -->
        <svg
          class="map"
          id="sample-map"
          xmlns="http://www.w3.org/2000/svg"
          :width="width"
          :height="height"
          :viewBox="`0 0 ${width} ${height}`"
          preserveAspectRatio="xMinYMin meet">
          <path
            v-for="country in countriesList"
            :key="country.feature.id"
            :d="pathGenerator(country.feature)"
            v-on:mouseover="countryHover($event, country.feature)"
            v-on:mouseleave="countryLeave"
            v-on:mousemove="mouseMoveOverMap"
            :style="{
              fill: country.color,
              stroke: defaultStrokeColor
            }"/>
        </svg>

      <!-- </pan-zoom> -->

    </div>

    <!-- summary -->
    <div v-if="haveGeoInsights">
      <div class="top-country-title mb-8 md:top-country-title">Top 5 countries</div>

      <div class="grid grid-flow-row md:grid-flow-rows grid-cols-1 gap-x-0.5 gap-y-4 md:grid-rows-3 md:grid-cols-2">
        <div class="flex items-center" :key="insight.country.name" v-for="insight in topFiveCountries">
          <div class="mr-2 top-country-scale-color" v-bind:style="{ 'background-color': `${scale(insight[selectedFilterKey].fixed)}` }"></div>
          <div class="mr-2 top-country-name whitespace-no-wrap"> {{ insight.country.name }}</div>
          <div class="top-country-percentage whitespace-no-wrap">{{ insight[selectedFilterKey].percentage + '%' }}</div>
        </div>

      </div>
    </div>

    <div class="tooltip" v-bind:style="toolTipStyle" v-if="showTooltip">

      <div v-if="haveGeoInsights">

        <div class="tooltip-head">
          <country-flag :country="currentCountry.country.code" size="normal"/>
          <span class="country-name">{{ currentCountry.country.name }}</span>
          <span class="percentage">{{ (currentCountry[selectedFilterKey].percentage || 0) | addComma('%') }}</span>
        </div>
        <div class="tooltip-row">
          <div class="tooltip-row-title">Spent</div>
          <div class="tooltip-row-body">{{ currentCountry.spent.fixed | addComma('$') }}</div>
        </div>
        <div class="tooltip-row">
          <div class="tooltip-row-title">Clicks</div>
          <div class="tooltip-row-body">{{ currentCountry.clicks.fixed | addComma }}</div>
        </div>
        <div class="tooltip-row">
          <div class="tooltip-row-title">Impressions</div>
          <div class="tooltip-row-body">{{ currentCountry.impressions.fixed | addComma }}</div>
        </div>

      </div>

      <div v-else>
        <slot name="empty-country-tooltip"></slot>
      </div>

    </div>
  </div>
</template>

<script>

import * as d3 from 'd3'
import {slice, reverse, sortBy, get, first, filter} from 'lodash'
import Color from 'color'
import countriesGeoJson from './data/countries.geo.json'
import CountryFlag from 'vue-country-flag'

export default {
  name: 'SpeakolChoroplethMap',
  components: {
    CountryFlag
  },
  data () {
    return {
      // panzoom //
      panZoomOptions: {
        bounds: true,
        transformOrigin: {x: 0.5, y: 0.5},
        maxZoom: 4,
        minZoom: 1,
        overflow: 'hidden',
        onTouch: () => {
          return false // tells the library to not preventDefault.
        }
      },
      panzoomInstance: null,
      zoomLevel: 1,

      // tooltip
      showTooltip: false,
      currentCountry: {
        country: {
          name: 'Egypt',
          code: 'EG'
        },
        impressions: {
          fixed: '214,087,38',
          percentage: '20'
        },
        clicks: {
          fixed: '100,465,298',
          percentage: '30'
        },
        spent: {
          fixed: '1207',
          percentage: '50'
        }
      },
      selectedFilter: null,
      clientX: '0px',
      clientY: '0px'
    }
  },

  props: {
    title: {
      type: String,
      default: 'Geographical Distribution'
    },
    width: {
      type: Number,
      default: 750
    },
    height: {
      type: Number,
      default: 500
    },
    defaultFillColor: {
      type: String,
      default: '#F0F0F0'
    },
    defaultStrokeColor: {
      type: String,
      default: '#F0F0F0'
    },
    filters: {
      type: Array
    },
    /**
     * [
     *    {
     *      color: 'red',
     *      text: 'Impressions',
     *      value: 'impressions'
     *    }
     *
     * ]
     */

    geoInsights: {
      type: Array
    }
    /**
     * [
     *    {
     *      "country": {
     *        "name": "",
     *        "code": ""
     *      },
     *      "impressions": {
     *         "fixed": "20",
     *         "percentage": ""
     *      },
     *      "clicks": {
     *         "fixed": "20",
     *         "percentage": ""
     *      },
     *      "spent": {
     *         "fixed": "20",
     *         "percentage": ""
     *      }
     *    }
     * ]
     */

  },
  computed: {
    topFiveCountries () {
      return slice(reverse(sortBy(this.geoInsights, `${this.selectedFilterKey}.fixed`)), 0, 5)
    },
    isFilterSelected () {
      return !!get(this, 'selectedFilter')
    },

    pathGenerator () {
      return d3.geoPath().projection(this.projection)
    },

    projection () {
      const {
        width,
        height
      } = this
      return d3.geoMercator().translate([350, 150]).fitExtent([[0, 0], [width, height]], countriesGeoJson)
    },

    countriesList () {
      return countriesGeoJson.features.map((feature) => {
        const insight = this.filterInsightsByPostalCode(feature.properties.postal)
        let color = this.defaultFillColor
        if (insight && this.selectedFilterKey) {
          color = this.scale(insight[this.selectedFilterKey].fixed)
        }
        return {
          feature,
          color
        }
      })
    },

    selectedFilterKey () {
      return get(this, 'selectedFilter.value')
    },

    highColor () {
      return get(this, 'selectedFilter.color', this.defaultFillColor)
    },

    lowColor () {
      return Color(this.highColor).fade(0.85).string()
    },

    gradiantStyle () {
      return {
        'background-image': `linear-gradient(to right, ${this.highColor}, ${this.lowColor})`
      }
    },


    haveGeoInsights () {
      return this.geoInsights.length > 0
    },

    scale () {
      const scale = d3.scaleSequential().range([this.lowColor, this.highColor])
      const {
        min,
        max
      } = this.range
      scale.domain([min, max])
      return scale
    },

    range () {
      const data = this.geoInsights.map(insight => {
        return insight[this.selectedFilterKey]
      })
      let min = Infinity
      let max = -Infinity
      for (let i = 0; i < data.length; i++) {
        if (data[i].fixed > max) {
          max = data[i].fixed
        }
        if (data[i].fixed < min) {
          min = data[i].fixed
        }
      }
      return {
        min,
        max
      }
    },

    toolTipStyle () {
      return {
        top: this.clientY,
        left: this.clientX
      }
    }
  },

  mounted () {
    this.setSelectedFilter(first(this.filters).value)
  },

  methods: {
    // panZoom //
    onInitPanZoom (panzoomInstance) {
      panzoomInstance.zoomAbs(0, 0, 1)
      this.panzoomInstance = panzoomInstance
    },

    zoomIn () {
      if (this.panzoomInstance && this.zoomLevel < 5) {
        this.zoomLevel++
        this.panzoomInstance.zoomAbs(0, 0, this.zoomLevel)
      }
    },

    zoomOut () {
      if (this.panzoomInstance && this.zoomLevel > 1) {
        this.zoomLevel--
        this.panzoomInstance.zoomAbs(0, 0, this.zoomLevel)
      }
    },

    countryHover (event, country) {
      if (this.haveGeoInsights) {
        this.currentCountry = this.filterInsightsByPostalCode(country.properties.postal)
        this.showTooltip = !!this.currentCountry
      } else {
        this.showTooltip = true
      }
    },

    mouseMoveOverMap (event) {
      this.clientX = `${event.clientX + 7}px`
      this.clientY = `${event.clientY + 7}px`
    },

    countryLeave () {
      this.showTooltip = false
    },

    setSelectedFilter (filterValue) {
      this.selectedFilter = first(filter(this.filters, filterItem => filterItem.value === filterValue))
    },


    filterInsightsByPostalCode (code) {
      const geoInsights = this.geoInsights
      return first(filter(geoInsights, (insight) => {
        return get(insight, 'country.code', '') === code
      }))
    },

    clearFilters () {
      this.selectedFilter = null
    }
  }

}
</script>


<style lang="scss" scoped>

.top-country-title {
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.1px;
  color: #2c2c2c;
}

.zoom-controller {
  position: absolute;
  width: 48px;
  height: 84px;
  z-index: 99;
  right: 20px;
  bottom: 20px;
  background: #fff;
  box-shadow: 4px 4px 100px 5px rgba(0, 0, 0, 0.1);
}

.zoom-in-icon {
  width: 24px;
  height: 42px;
  border-bottom: 1px solid #e3e3e3;
  cursor: pointer;
}

.zoom-out-icon {
  width: 24px;
  height: 42px;
  font-size: 18px;
  font-weight: 500;
  cursor: pointer;
}

.card {
  background-color: white;
}

.title {
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.1px;
  color: #2c2c2c;
}

.gradiant {
  width: 190px;
  height: 10px;
  border-radius: 10px;
}

.gradiant-label {
  font-style: normal;
  font-weight: 500;
  font-size: 14px;
  line-height: 21px;
  letter-spacing: 0.1px;
  color: rgba(44, 44, 44, 0.56);
}

.tooltip {
  position: fixed;
  min-width: 145px;
  height: auto;
  background: #fff;
  border-radius: 3px;
  padding: 15px;
  box-shadow: 0 0 8px #eee;
  z-index: 999;

  .tooltip-head {
    margin: 0 0 8px 0;

    .normal-flag {
      transform: scale(0.4) !important;
      -ms-transform: scale(0.4) !important;
      -webkit-transform: scale(0.4) !important;
      -moz-transform: scale(0.4) !important;
      margin: -8px -19px;
      float: left;
    }

    .country-name {
      font-size: 14px;
      color: rgba(44, 44, 44, 0.56);
      margin: 0 8px;
    }

    .percentage {
      font-size: 16px;
      font-weight: 500;
      color: #2c2c2c;
    }
  }

  .tooltip-row {
    padding: 7px 0;

    .tooltip-row-title {
      font-size: 14px;
      color: rgba(44, 44, 44, 0.56);
      margin-bottom: 5px;
    }

    .tooltip-row-body {
      font-size: 16px;
      font-weight: 500;
      color: #2c2c2c;
    }
  }
}

.top-country-scale-color {
  width: 24px;
  height: 16px;
}

.top-country-name {
  font-style: normal;
  font-weight: normal;
  font-size: 14px;
  line-height: 21px;
  letter-spacing: 0.1px;
  color: rgba(44, 44, 44, 0.56);
}

.top-country-percentage {
  font-style: normal;
  font-weight: 500;
  font-size: 16px;
  line-height: 24px;
  letter-spacing: 0.1px;
  color: #2c2c2c;
}

@media (min-width: 768px) {
  .md\:top-country-title {
    font-style: normal;
    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
    letter-spacing: 0.1px;
    color: #2c2c2c;
  }

  .md\:title {
    font-style: normal;
    font-weight: normal;
    font-size: 14px;
    line-height: 36px;
    color: #2c2c2c;
    opacity: 0.3;
  }
}
</style>
